import { useCallback, useState } from "react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/form/Button";
import useTranslations from "@/shared/lang/useTranslations";

type DeletePublicAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
  onConfirm: () => void | Promise<void>;
};

export function DeletePublicAccountModal({
  isOpen,
  onClose,
  accountName,
  onConfirm,
}: DeletePublicAccountModalProps) {
  const { t } = useTranslations("accountsWallets");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [onClose, isSubmitting]);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm, onClose]);

  const footer = (
    <div className="flex flex-col space-y-4 w-full pt-1">
      <Button
        type="button"
        variant="danger"
        onClick={handleConfirm}
        disabled={isSubmitting}
        isLoading={isSubmitting}
        className="w-full py-3.5 rounded-xl font-bold active:scale-[0.98]"
      >
        {t("delete_account_confirm")}
      </Button>
      <button
        type="button"
        onClick={handleClose}
        disabled={isSubmitting}
        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors"
      >
        {t("modal_cancel")}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("delete_account_modal_title")}
      size="sm"
      footer={footer}
    >
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t("delete_account_modal_message", { name: accountName })}
      </p>
    </Modal>
  );
}
